import { defineComponent as _defineComponent } from 'vue'
import CloseButton from '@/components/buttons/dedicated/CloseButton.vue';
import TextInput from '@/components/inputs/TextInput.vue';
import BubbleIcon from '@/components/interface/BubbleIcon.vue';
import localization from '@/i18n';
import { dateTimeFormats } from '@/lang/dateTimeFormats';
import {
  getPackagePriceByProductLookupKey,
  getPackagePricesByType,
  getPackageQuota,
  getSubscriptionPriceByProductLookupKey,
  getSubscriptionPricesByType,
  getSubscriptionQuota,
} from '@/lib/billing/billingFunctions';
import { Icon } from '@/lib/enum/Icon';
import store from '@/store';
import { convertPenceToPounds, formatCurrency } from '@/util/currencyFunctions';
import spacetime from 'spacetime';
import { computed, ref } from 'vue';
import {
  BillingPackage,
  BillingPackageIntervalEnum,
  BillingProductTypeEnum,
  BillingQuotaDefinitionLookupKeyEnum,
  BillingSubscription,
  SubscriptionQuote,
} from '../../../../api/v1';


export default /*#__PURE__*/_defineComponent({
  __name: 'BillingSummary',
  props: {
    billingPackage: null,
    subscription: { default: null },
    quote: { default: null },
    appliedOffers: { default: () => [] }
  },
  emits: ["addOffer", "removeOffer"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'addOffer', offerCode: string): void;
  (e: 'removeOffer', offerCode: string): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { billingPackage: BillingPackage | null, subscription: BillingSubscription, quote: SubscriptionQuote, appliedOffers: string[] }





const code = ref('');
const timezone = computed(() => store.getters.timezone);
const isTrialVersion = computed(() => store.getters['billing/isTrialVersion']);
const billingPackage = computed(
  () => props.billingPackage || store.getters['billing/billingPackage'],
);
const currentSubscription = computed(
  () => store.state.billing.currentSubscription,
);

const isRestore = computed(
  () => store.state.billing.billingDialogState.isRestore,
);
const isBillingOnly = computed(
  () => store.state.billing.billingDialogState.isBillingOnly,
);

const packagePrice = computed(() => {
  if (props.billingPackage) {
    return (
      (getPackagePricesByType(
        props.billingPackage,
        BillingProductTypeEnum.Plan,
      )[0]?.fixedPrice || 0) / 100
    );
  }

  return (
    (getSubscriptionPricesByType(
      props.subscription,
      BillingProductTypeEnum.Plan,
    )[0]?.fixedPrice || 0) / 100
  );
});

const employeeSoftLimit = computed(() => {
  if (props.billingPackage) {
    return getPackageQuota(
      props.billingPackage,
      BillingQuotaDefinitionLookupKeyEnum.EmployeeSoftLimit,
    );
  }

  return getSubscriptionQuota(
    props.subscription,
    BillingQuotaDefinitionLookupKeyEnum.EmployeeSoftLimit,
  );
});

const additionalEmployeePrice = computed(() => {
  if (props.billingPackage) {
    return getPackagePriceByProductLookupKey(props.billingPackage, 'employees');
  }

  return getSubscriptionPriceByProductLookupKey(
    props.subscription,
    'employees',
  );
});

const additionalEmployeesText = computed(() => {
  if (additionalEmployeePrice.value.fixedPrice !== null) {
    return localization.tc('signUp.billing.additionalMembers', 1, {
      cost: formatCurrency(additionalEmployeePrice.value.fixedPrice, 'GBP'),
    });
  }

  const { tiers } = additionalEmployeePrice.value;
  const lines = tiers.map((t) => {
    if (t.quantityEnd === null) {
      return localization.tc('signUp.billing.additionalMembersTierLast', 1, {
        start: t.quantityStart,
        rate: formatCurrency(convertPenceToPounds(t.unitPrice), 'GBP'),
      });
    }

    return localization.tc('signUp.billing.additionalMembersTierN', 1, {
      start: t.quantityStart,
      end: t.quantityEnd,
      rate: formatCurrency(convertPenceToPounds(t.unitPrice), 'GBP'),
    });
  });

  const employeeSoftLimit = getSubscriptionQuota(
    currentSubscription.value,
    BillingQuotaDefinitionLookupKeyEnum.EmployeeSoftLimit,
  );
  const isPayPerEmployeePlan = employeeSoftLimit === 0;

  lines.splice(
    0,
    0,
    isPayPerEmployeePlan
      ? localization.tc('info.account.employeesBilledAtRates')
      : localization.tc('info.account.additionalEmployeesBilledAtRates'),
  );

  return lines.join('\n');
});

const rejectedOffers = computed(() => [
  ...new Set([
    ...props.appliedOffers.filter(
      (offer) => !props.quote?.appliedOffers.includes(offer),
    ),
    ...(props.quote?.unusedOffers ?? []),
  ]),
]);

const paymentDateRange = computed(() => {
  const startDate = props.quote.expectedStartDate.toDate(timezone.value);
  const endDate = props.quote.expectedRenewalDate.toDate(timezone.value);

  const endsNextYear = !spacetime(endDate, timezone.value).isSame(
    spacetime.now(timezone.value),
    'year',
  );
  const startString = localization.d(
    startDate,
    endsNextYear ? dateTimeFormats.dayMonthYear : dateTimeFormats.dayMonth,
  );
  const endString = localization.d(endDate, dateTimeFormats.dayMonthYear);

  return `${startString} ${localization.tc('conjunction.to', 1)} ${endString}`;
});

const paymentText = computed(() => {
  if (isTrialVersion.value) {
    return localization.tc('signUp.billing.remainingTrialDays', 1, {
      name: billingPackage.value.name,
    });
  }

  return billingPackage.value.interval === BillingPackageIntervalEnum.Annual
    ? localization.tc('signUp.billing.chargeFor', 1, {
        name: billingPackage.value.name,
      })
    : localization.tc('signUp.billing.proRataFor', 1, {
        name: billingPackage.value.name,
      });
});

const addCode = (offerCode: string) => {
  emit('addOffer', offerCode);
  code.value = '';
};

return { __sfc: true,props, emit, code, timezone, isTrialVersion, billingPackage, currentSubscription, isRestore, isBillingOnly, packagePrice, employeeSoftLimit, additionalEmployeePrice, additionalEmployeesText, rejectedOffers, paymentDateRange, paymentText, addCode, CloseButton, TextInput, BubbleIcon, dateTimeFormats, Icon, formatCurrency, BillingPackageIntervalEnum }
}

})